import { attributes } from '../../core/ids-attributes';
import { customElement, scss } from '../../core/ids-decorators';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import IdsElement from '../../core/ids-element';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import styles from './ids-toolbar-section.scss';

const TOOLBAR_SECTION_ATTRIBUTES = [
  attributes.ALIGN,
  attributes.FAVOR,
  attributes.FLEX_GROW,
  attributes.INACTIVE,
  attributes.KEEP_VISIBLE,
  attributes.TOOLBAR_TYPE,
  attributes.TYPE
];

// Alignment styles that can apply to a section
const SECTION_ALIGNS = [
  'align-start',
  'align-center',
  'align-center-even',
  'align-end'
];

// Toolbar types
const TOOLBAR_TYPES = ['formatter'];

// Section types
const SECTION_TYPES = [
  'static',
  'buttonset',
  'button',
  'fluid',
  'search',
  'title',
  'more'
];

// WebComponent Tagnames that correspond to valid toolbar items
const TOOLBAR_ITEM_TAGNAMES = [
  'ids-button',
  'ids-checkbox',
  'ids-input',
  'ids-menu-button',
  'ids-radio',
  'ids-search-field',
  'ids-segmented-control',
  'ids-toolbar-more-actions',
  'ids-tabs-context'
];

// WebComponent Tagnames that correspond to valid text nodes
const TOOLBAR_TEXTNODE_TAGNAMES = [
  'ids-text'
];

// WebComponent Tagnames that correspond to valid ids-separator nodes
const TOOLBAR_SEPARATOR_TAGNAMES = [
  'ids-separator'
];

/**
 * Checks an element's CSS classlist for an item belonging to a group,
 * appends that item, and removes all others from the group.
 * @private
 * @param {string} targetClass the target CSS class to apply/check
 * @param {HTMLElement} targetElem the target elem on which to apply the class
 * @param {Array<string>} group the array of classes to scan for removal
 */
function setCssClassFromGroup(targetClass: string, targetElem: HTMLElement, group: Array<string>): void {
  group.forEach((item) => {
    const cl = targetElem?.classList;
    const cssClass = `${item}`;
    const thisClass = `${targetClass}`;

    if (cssClass === thisClass) {
      if (!cl?.contains(cssClass)) {
        cl?.add(cssClass);
      }
    } else {
      cl?.remove(cssClass);
    }
  });
}

const Base = IdsEventsMixin(
  IdsElement
);

/**
 * IDS Toolbar Section Component
 * @type {IdsToolbarSection}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 */
@customElement('ids-toolbar-section')
@scss(styles)
export default class IdsToolbarSection extends Base {
  constructor() {
    super();
  }

  static get attributes(): Array<string> {
    return [...super.attributes, ...TOOLBAR_SECTION_ATTRIBUTES];
  }

  template(): string {
    const align = this.align ? ` align-${this.align}` : '';
    const pinnable = this.pinnable ? ` pinnable` : '';
    return `
      <div class="ids-toolbar-section${align}${pinnable}" part="container">
        <slot></slot>
      </div>
    `;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.validateDefaultType();
    setCssClassFromGroup(this.type, this.container as HTMLElement, SECTION_TYPES);
    setCssClassFromGroup(this.toolbarType ?? '', this.container as HTMLElement, TOOLBAR_TYPES);
    this.#observeChildVisibility();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
    this.#childObserver?.disconnect();
    this.#childObserver = null;
  }

  /**
   * @readonly
   * @returns {Array<any>} list of available items contained by the toolbar
   */
  get items(): Array<HTMLElement> {
    const children = this.querySelectorAll('*');
    return [...children].filter((e): e is HTMLElement => {
      const elemTagName = e.tagName.toLowerCase();
      if (!TOOLBAR_ITEM_TAGNAMES.includes(elemTagName)) {
        return false;
      }
      // Skip items with display: none
      const htmlElement = e as HTMLElement;
      return htmlElement.style.display !== 'none';
    }) as HTMLElement[];
  }

  /**
   * @readonly
   * @returns {Array<Element>} list of text nodes contained by the toolbar section
   */
  get textElems(): Array<Element> {
    return [...this.children].filter((e) => {
      const elemTagName = e.tagName.toLowerCase();
      return TOOLBAR_TEXTNODE_TAGNAMES.includes(elemTagName);
    });
  }

  /**
   * @readonly
   * @returns {Array<Element>} list of ids-separator nodes contained by the toolbar section
   */
  get separators(): Array<Element> {
    return [...this.children].filter((e) => {
      const elemTagName = e.tagName.toLowerCase();
      return TOOLBAR_SEPARATOR_TAGNAMES.includes(elemTagName);
    });
  }

  /**
   * @readonly
   * @returns {HTMLElement} a reference to this section's toolbar parent node
   */
  get toolbar(): HTMLElement {
    return this.parentElement as HTMLElement;
  }

  /**
   * @param {string} val the alignment type to set
   */
  set align(val: string) {
    let trueVal = `align-${val}`;
    if (typeof val !== 'string' || val === '' || !SECTION_ALIGNS.includes(trueVal)) {
      this.removeAttribute('align');
      trueVal = SECTION_ALIGNS[0];
    } else {
      this.setAttribute('align', val);
      this.container?.classList.add(`align-${val}`);
    }
  }

  /**
   * @returns {string} the current alignment value
   */
  get align(): string {
    return this.getAttribute(attributes.ALIGN) || 'start';
  }

  /**
   * @param {boolean | string} val true if this toolbar section should be marked "favor"
   * (will try not to be collapsed/shrunk if the parent toolbar size shrinks)
   */
  set favor(val: boolean | string) {
    const newValue = stringToBool(val);
    if (newValue) {
      this.setAttribute(attributes.FAVOR, '');
      this.container?.classList.add(attributes.FAVOR);
    } else {
      this.removeAttribute(attributes.FAVOR);
      this.container?.classList.remove(attributes.FAVOR);
    }
  }

  /**
   * @returns {boolean} true if this toolbar section is marked "favor"
   * (will try not to be collapsed/shrunk if the parent toolbar size shrinks)
   */
  get favor(): boolean {
    return this.hasAttribute(attributes.FAVOR);
  }

  /**
   * @param {string | boolean | null} value truthy if this toolbar section should be made "inactive"
   * (takes up space, but items are not visible)
   */
  set inactive(value: string | boolean | null) {
    const isInactive = stringToBool(value);
    this.toggleAttribute(attributes.INACTIVE, isInactive);
    this.container?.classList.toggle(attributes.INACTIVE, isInactive);
  }

  /**
   * @returns {boolean} true if the toolbar section is "inactive" (takes up space, but items are not visible)
   */
  get inactive(): boolean {
    return this.hasAttribute(attributes.INACTIVE);
  }

  /**
   * @param {string} val the type of section
   */
  set type(val: string) {
    let trueVal;
    if (typeof val !== 'string' || val === '' || !SECTION_TYPES.includes(val)) {
      trueVal = SECTION_TYPES[0];
    } else {
      trueVal = `${val}`;
    }
    this.setAttribute(attributes.TYPE, trueVal);

    if (this.container) {
      setCssClassFromGroup(trueVal, this.container, SECTION_TYPES);
    }
  }

  /**
   * @returns {string} the type of section
   */
  get type(): string {
    return this.getAttribute(attributes.TYPE) || 'static';
  }

  /**
   * @param {string} value the type of toolbar
   */
  set toolbarType(value: string | null) {
    if (value && TOOLBAR_TYPES.includes(value)) {
      this.setAttribute(attributes.TOOLBAR_TYPE, value);
      this.container?.classList.add(value);
    } else {
      this.removeAttribute(attributes.TOOLBAR_TYPE);
      this.container?.classList.remove(TOOLBAR_TYPES[0]);
    }
  }

  /**
   * @returns {string} the type of toolbar
   */
  get toolbarType(): string | null {
    return this.getAttribute(attributes.TOOLBAR_TYPE);
  }

  /**
   * @param {string | number | null} val the flex-grow value (e.g., 0, 1, 2)
   */
  set flexGrow(val: string | number | null) {
    const numVal = val !== null ? Number(val) : null;
    if (numVal !== null && !Number.isNaN(numVal)) {
      this.setAttribute(attributes.FLEX_GROW, String(numVal));
      this.style.setProperty(attributes.FLEX_GROW, String(numVal));
    } else {
      this.removeAttribute(attributes.FLEX_GROW);
      this.style.removeProperty(attributes.FLEX_GROW);
    }
  }

  /**
   * @returns {number | null} the flex-grow value as a number
   */
  get flexGrow(): number | null {
    const val = this.getAttribute(attributes.FLEX_GROW);
    return val !== null ? Number(val) : null;
  }

  /**
   * @param {boolean} val true if this section will be pinnable (the items inside will be pinnable)
   */
  set pinnable(val: boolean) {
    this.toggleAttribute(attributes.PINNABLE, stringToBool(val));
  }

  get pinnable(): boolean {
    return stringToBool(this.getAttribute(attributes.PINNABLE));
  }

  /**
   * @returns {Array<Element>} val list of items that have the pinned status
   */
  get pinned(): Array<Element> {
    return this.items.filter((e) => e.hasAttribute(attributes.PINNED));
  }

  /**
   * @param {boolean} val true if this section should always remain visible even when empty
   */
  set keepVisible(val: boolean) {
    const newVal = stringToBool(val);
    this.toggleAttribute(attributes.KEEP_VISIBLE, newVal);
    if (newVal) {
      this.classList.remove('empty');
    } else {
      this.#checkChildVisibility();
    }
  }

  /**
   * @returns {boolean} true if this section will not be auto-hidden when empty
   */
  get keepVisible(): boolean {
    return this.hasAttribute(attributes.KEEP_VISIBLE);
  }

  /**
   * Validates default type to filter section with searchfields.
   */
  validateDefaultType(): void {
    // Static Types will cause issue on header searchfields in Mobile View
    const children = Array.from(this.children);
    if (children.length === 1 && children[0].tagName === 'IDS-SEARCH-FIELD') {
      this.type = 'search';
    }
  }

  /** MutationObserver for tracking child visibility changes */
  #childObserver: MutationObserver | null = null;

  /**
   * Observe child additions/removals and hidden attribute changes
   * to toggle the `empty` class on the host element
   */
  #observeChildVisibility() {
    this.#childObserver = new MutationObserver(() => this.#checkChildVisibility());
    this.#childObserver.observe(this, {
      childList: true,
      attributes: true,
      attributeFilter: ['hidden'],
      subtree: true
    });
    this.#checkChildVisibility();
  }

  /**
   * Toggle the `empty` class based on whether any child is visible
   */
  #checkChildVisibility() {
    if (this.keepVisible) return;
    const children = Array.from(this.children);
    const hasVisibleChild = children.some((child) => !child.hasAttribute('hidden'));
    this.classList.toggle('empty', !hasVisibleChild);
  }
}

export {
  TOOLBAR_ITEM_TAGNAMES
};
